/**
 * @fileoverview "Add a link" modal of the student deliverable drawer (AR-678).
 * Opens centered ON TOP of the drawer (Mantine Modal z-index > Rodal drawer).
 * Validates client-side (`normalizeLinkUrl`) and routes the backend outcome:
 * 422 → inline error on the input; other failures → the parent toasts and the
 * modal stays open for a retry. Accidental-dismiss guard: with typed text or
 * an in-flight POST, outside-click/Escape no longer close — only the explicit
 * header X / Cancel do (and never while submitting). State is reset on every
 * open/close so attempts never leak.
 * Local copy — the TaskTracker drawer keeps its own (copy per module).
 */

import { useCallback, useEffect, useState } from 'react'
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { normalizeLinkUrl, type AddLinkOutcome } from './linkUtils'

/** Mirrors the backend column limit for the link URL. */
const LINK_URL_MAX_LENGTH = 2048

interface AddLinkModalProps {
  opened: boolean
  onClose: () => void
  /** POSTs the normalized URL; resolves with the outcome used to route feedback. */
  onAdd: (url: string) => Promise<AddLinkOutcome>
}

export function AddLinkModal({ opened, onClose, onAdd }: AddLinkModalProps) {
  const { t } = useTranslation()
  const tr = (key: string) => t(`student_tasks.deliverable_drawer.${key}`)

  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Fresh state on every open, so a previous attempt never leaks in.
  useEffect(() => {
    if (opened) {
      setValue('')
      setError(null)
    }
  }, [opened])

  // Close (header X / Cancel / outside click): allowed unless the POST is in
  // flight; discards whatever was typed so the next open starts clean.
  const handleClose = useCallback(() => {
    if (submitting) return
    setValue('')
    setError(null)
    onClose()
  }, [submitting, onClose])

  const handleSubmit = async () => {
    if (submitting) return
    const normalized = normalizeLinkUrl(value)
    if (!normalized) {
      setError(tr('link_invalid'))
      return
    }
    setSubmitting(true)
    try {
      const outcome = await onAdd(normalized)
      if (outcome === 'invalid') {
        setError(tr('link_invalid'))
        return
      }
      if (outcome === 'success') {
        setValue('')
        setError(null)
        onClose()
      }
      // 'error': the parent already toasted; keep the modal open for a retry.
    } finally {
      setSubmitting(false)
    }
  }

  // Accidental-dismiss guard: once something is typed (or while submitting),
  // outside clicks and Escape stop closing the modal.
  const allowImplicitClose = !submitting && value.trim() === ''

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={tr('add_link_modal_title')}
      centered
      closeOnClickOutside={allowImplicitClose}
      closeOnEscape={allowImplicitClose}
    >
      <Stack spacing="md">
        <TextInput
          label={tr('link_url_label')}
          placeholder={tr('link_url_placeholder')}
          value={value}
          onChange={event => {
            setValue(event.currentTarget.value)
            setError(null)
          }}
          error={error}
          maxLength={LINK_URL_MAX_LENGTH}
          data-autofocus
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleSubmit()
            }
          }}
        />
        <Group position="right" spacing="xs">
          <Button variant="default" onClick={handleClose} disabled={submitting}>
            {tr('cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {tr('add')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
